import { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import CanonicalTag from '../components/CanonicalTag';
import beautyStudioImage from '../assets/beauty-studio-adu.jpg';

const HowToUseADUBeautyStudio = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);
  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-backyard-barber-or-beauty-studio" />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-6">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
                How to Use Your ADU as a Backyard Barber or Beauty Studio
              </h1>
              <div className="flex justify-center mb-8">
                <button 
                  onClick={openCalendlyModal}
                  className="bg-brand-orange text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                >
                  📅 Book & Schedule
                </button>
              </div>
            </div>

            {/* Main Image */}
            <div className="mb-16">
              <img 
                src={beautyStudioImage} 
                alt="ADU Beauty Studio" 
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Content Sections */}
            <div className="space-y-16">
              {/* Section 1 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Open Your Own Chair—Steps from Home
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Turn your backyard into a fully functional barber shop, salon, or lash studio. Run your own business, manage clients professionally, and stay close to home.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Professional Setup</h3>
                    <p className="text-muted-foreground">Clean, client-ready environment with private entrance and waiting area</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Full Equipment</h3>
                    <p className="text-muted-foreground">Fully equipped shampoo station, wall mirrors, and professional lighting</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Comfort Features</h3>
                    <p className="text-muted-foreground">Anti-fatigue mats, proper ventilation, and client comfort amenities</p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => window.location.href = '/landing'}
                    className="bg-white text-brand-orange border-2 border-brand-orange px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange hover:text-white transition-colors"
                  >
                    📐 What Fits My Lot?
                  </button>
                  <button 
                    onClick={() => window.location.href = '/adu-calculator'}
                    className="bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                  >
                    💰 Get Your Price & ROI Report
                  </button>
                </div>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Perfect for Stylists, Barbers, Lash Techs, and Estheticians
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl font-semibold mb-4" style={{ color: '#E4683B' }}>Business Features</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>• Clean, client-ready environment</li>
                      <li>• Private entrance + waiting chair</li>
                      <li>• Fully equipped shampoo station or treatment sink</li>
                      <li>• Wall mirrors, anti-fatigue mats, and lighting options</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-4" style={{ color: '#E4683B' }}>Business Benefits</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>• Flexible hours without rent</li>
                      <li>• Tax-deductible business space</li>
                      <li>• Excellent resale potential with live/work zoning</li>
                      <li>• Soundproofing & sanitation friendly</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Soundproofing & Sanitation Friendly
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Cleanable surfaces, separate HVAC, towel storage. Easy client entry/exit via private path.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Sanitation</h3>
                    <p className="text-muted-foreground">Easy-to-clean surfaces and proper drainage for professional hygiene standards</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Privacy</h3>
                    <p className="text-muted-foreground">Soundproofed walls and separate entrance for client discretion</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Storage</h3>
                    <p className="text-muted-foreground">Built-in storage for supplies, towels, and professional equipment</p>
                  </div>
                </div>
              </section>
            </div>

            {/* Bottom CTAs */}
            <div className="text-center mt-16">
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button 
                  onClick={() => window.open('/our-work', '_blank')}
                  className="bg-white text-brand-orange border-2 border-brand-orange px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange hover:text-white transition-colors"
                >
                  🏡 Our Portfolio
                </button>
                <button 
                  onClick={() => window.open('https://uda-adu.com/faq', '_blank')}
                  className="bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                >
                  ❓ Our FAQ
                </button>
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[90vh] m-4">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold">Schedule a Consultation</h3>
                <button 
                  onClick={closeCalendlyModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <iframe 
                src="https://calendly.com/udaadu"
                width="100%" 
                height="100%" 
                frameBorder="0"
                title="Schedule a meeting"
                className="rounded-b-lg"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HowToUseADUBeautyStudio;